// Onglet « Assistant » — les instructions générales et le plafond.
//
// Les instructions générales sont relues à CHAQUE réponse de l'assistant,
// en fin de prompt (docs/OUTIL-ASSISTANT.md §2) : « pose une question à
// la fois », « réponds en trois lignes »… Une modification vaut dès le
// message suivant, sans déploiement.

import { useCallback, useEffect, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import { Textarea } from '../../ui/TextField.jsx';
import NumberField from '../../ui/NumberField.jsx';
import Button from '../../ui/Button.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';

export function AssistantPanel({ isAdmin }) {
  const toast = useToast();
  const load = useCallback(() => api.get('/parametres'), []);
  const { data, error, loading, reload } = useResource(load);
  const [form, setForm] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (data) setForm({ instructions_generales: '', plafond_messages_jour: 100, ...(data.settings.assistant ?? {}) });
  }, [data]);

  if (loading || !form) return <SkeletonRows rows={4} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  async function save(event) {
    event.preventDefault();
    setPending(true);
    try {
      await api.put('/parametres/assistant', {
        instructions_generales: form.instructions_generales ?? '',
        plafond_messages_jour: form.plafond_messages_jour ?? 100,
      });
      toast.success('Réglages de l’assistant enregistrés.');
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <Card>
      <CardHead title="Assistant">
        {isAdmin ? (
          <Button size="sm" variant="primary" type="submit" form="form-assistant" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        ) : null}
      </CardHead>
      <CardBody>
        <form id="form-assistant" className="stack" onSubmit={save}>
          <Textarea
            label="Instructions générales"
            hint="Relues à chaque réponse. Exemples : « réponds en trois lignes au plus », « cite toujours le client »."
            rows={6}
            maxLength={4000}
            value={form.instructions_generales ?? ''}
            disabled={!isAdmin}
            onChange={(e) => setForm({ ...form, instructions_generales: e.target.value })}
          />
          <NumberField
            label="Plafond de messages par jour et par personne"
            unit="messages"
            min={1}
            max={1000}
            value={form.plafond_messages_jour}
            disabled={!isAdmin}
            onChange={(v) => setForm({ ...form, plafond_messages_jour: v })}
          />
          <p className="field__hint">
            Au-delà du plafond, l’assistant le dit au lieu de répondre. Son coût se lit à part,
            par mois, dans l’État du service.
          </p>
        </form>
      </CardBody>
    </Card>
  );
}

export default AssistantPanel;
